import React, { PropsWithChildren, useMemo, useState, useLayoutEffect } from 'react';
import { createTheme, ThemeProvider, CssBaseline } from '@mui/material';
import { ColorModeContext } from '../context/ColorModeContext';
import { color, font, radius, border } from '../theme/tokens';

/**
 * ColorModeProvider — Nokia 8-bit theme with monochrome ramp + 3 accents.
 *
 * - Base font is VT323/Nokia FC22 (not Roboto) — fixes S-03 typography drift.
 * - Dark mode is the default (Nokia screen-off aesthetic).
 * - data-color-mode attribute is set synchronously via useLayoutEffect AND
 *   inlined into <body> from index.html to eliminate first-paint flash.
 * - Disabled button state uses #666 on #999 border (WCAG AA, fixes S-04).
 */

const STORAGE_KEY = 'wipe-color-mode';

// Read the preference synchronously before React renders so the stored-mode
// match on first paint is deterministic. Default is light (the dark palette
// needs more contrast tuning before it can ship as default — tracked in
// .planning/DEFERRED.md). Users can still toggle to dark via the topbar.
function readInitialMode(): 'dark' | 'light' {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark') return 'dark';
    return 'light';
  } catch {
    return 'light';
  }
}

export const ColorModeProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [mode, setMode] = useState<'dark' | 'light'>(readInitialMode);
  const darkMode = mode === 'dark';

  // Set attribute synchronously BEFORE browser paint to avoid the
  // light-on-dark flash (S-02). Mirror it onto <html> AS WELL as <body>
  // because index.html's fallback background CSS keys off
  // `html[data-color-mode="dark"]` — without the html mirror, toggling
  // the theme in-app leaves the html bg out of sync with the MUI body
  // palette, producing a half-and-half look in scrollbar/overscroll areas.
  useLayoutEffect(() => {
    document.body.setAttribute('data-color-mode', mode);
    document.documentElement.setAttribute('data-color-mode', mode);
  }, [mode]);

  const toggleColorMode = () => {
    setMode((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* ignore quota errors */
      }
      return next;
    });
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: { main: darkMode ? color.walletAmber : color.pixelBlack },
          secondary: { main: color.walletAmber },
          ...(darkMode
            ? {
                background: { default: color.pixelBlack, paper: color.pixelGraphite },
                text: {
                  primary: color.textDark,
                  secondary: color.textDarkMuted,
                  // text.disabled defaults to rgba(255,255,255,0.5) which fails AA;
                  // pin to a token-safe muted-but-readable value.
                  disabled: '#9a9a9a',
                },
                divider: color.pixelAsh,
                warning: { main: color.walletAmber },
                error: { main: color.deathRed },
                success: { main: color.sparkGreen },
              }
            : {
                background: { default: color.pixelPaper, paper: color.pixelPaper },
                text: {
                  primary: color.textLight,
                  secondary: color.textLightMuted,
                  disabled: '#666',
                },
                divider: color.pixelAsh,
                warning: { main: color.walletAmber },
                error: { main: color.deathRed },
                success: { main: color.sparkGreen },
              }),
        },
        typography: {
          // Flip base font from Roboto → Nokia. Fixes S-03 typography drift.
          fontFamily: font.body,
          // Fluid `clamp()` scale from the prior theme — still the right call
          // for small-phone to desktop without stair-step breakpoints.
          h1: { fontFamily: font.display, letterSpacing: 2, fontSize: 'clamp(2rem, 5vw + 0.5rem, 3.75rem)', lineHeight: 1.1 },
          h2: { fontFamily: font.display, letterSpacing: 2, fontSize: 'clamp(1.75rem, 4vw + 0.4rem, 3rem)', lineHeight: 1.15 },
          h3: { fontFamily: font.display, letterSpacing: 1, fontSize: 'clamp(1.5rem, 3vw + 0.4rem, 2.5rem)', lineHeight: 1.2 },
          h4: { fontFamily: font.display, letterSpacing: 1, fontSize: 'clamp(1.25rem, 2vw + 0.4rem, 2rem)', lineHeight: 1.25 },
          h5: { fontFamily: font.display, letterSpacing: 1, fontSize: 'clamp(1.1rem, 1.5vw + 0.4rem, 1.6rem)', lineHeight: 1.3 },
          h6: { fontFamily: font.display, letterSpacing: 0.5, fontSize: 'clamp(1rem, 1vw + 0.4rem, 1.3rem)', lineHeight: 1.35 },
          body1: { fontSize: 'clamp(0.95rem, 0.25vw + 0.9rem, 1.05rem)', lineHeight: 1.55 },
          body2: { fontSize: 'clamp(0.88rem, 0.2vw + 0.82rem, 0.98rem)', lineHeight: 1.55 },
          // Bumped minimum caption size from 0.72rem → 0.88rem for legibility
          // at the pixel font scale and to tighten contrast vs. body.
          caption: { fontSize: 'clamp(0.88rem, 0.15vw + 0.82rem, 0.95rem)', lineHeight: 1.45 },
          overline: { fontFamily: font.display, letterSpacing: 2, fontSize: '0.82rem', textTransform: 'uppercase' },
          button: { fontFamily: font.display, letterSpacing: 1, textTransform: 'uppercase' },
        },
        shape: { borderRadius: radius.card },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              // Explicit body background. MUI's CssBaseline does set this
              // from `palette.background.default` by default, but its merge
              // behavior with custom `body` overrides has burned us before
              // (the body would render the dark default while the rest of
              // the app painted light, producing the half-and-half bug).
              // Setting bg + color here forces the issue.
              html: {
                backgroundColor: darkMode ? color.pixelBlack : color.pixelPaper,
              },
              body: {
                fontFamily: font.body,
                backgroundColor: darkMode ? color.pixelBlack : color.pixelPaper,
                color: darkMode ? color.textDark : color.textLight,
                minHeight: '100vh',
              },
              '#root': {
                backgroundColor: darkMode ? color.pixelBlack : color.pixelPaper,
                minHeight: '100vh',
              },
              // Raw <a> elements (react-router-dom Link, plain markup) inherit
              // the browser default link color which fails WCAG AA against
              // pixelPaper. Force them onto the contrast-safe link tokens.
              a: {
                color: darkMode ? color.linkDark : color.linkLight,
                textDecorationColor: 'currentColor',
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: radius.card,
                border: border.arcade,
                boxShadow: 'none',
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: { backgroundImage: 'none', boxShadow: 'none' },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                fontFamily: font.display,
                fontVariantNumeric: 'tabular-nums',
                borderRadius: radius.pixel,
                letterSpacing: 0.5,
              },
              // Override the auto-generated success/warning/error chip text
              // colors to use our hand-tuned WCAG-AA tokens instead of MUI's
              // default light-shade variants which fail contrast at chip size.
              colorSuccess: { color: color.sparkGreen, borderColor: color.sparkGreen },
              colorWarning: { color: color.walletAmber, borderColor: color.walletAmber },
              colorError: { color: color.deathRed, borderColor: color.deathRed },
            },
          },
          MuiButton: {
            defaultProps: { disableElevation: true, disableRipple: true },
            styleOverrides: {
              root: {
                borderRadius: radius.flat,
                textTransform: 'uppercase',
                fontWeight: 400,
                fontFamily: font.display,
                letterSpacing: 1,
                border: '2px solid currentColor',
                boxShadow: 'none',
                transition: 'transform 80ms steps(2)',
                '&:hover': { transform: 'translate(1px, 1px)', boxShadow: 'none' },
                '&:active': { transform: 'translate(2px, 2px)' },
                '&.Mui-disabled': {
                  // WCAG AA pair (fixes S-04 + L-01):
                  // #666 on #f9f7f0 = 6.1:1, #999 border = 3.1:1 (non-text AA ≥3).
                  borderColor: '#999',
                  color: '#666',
                },
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                borderRadius: radius.flat,
                fontFamily: font.body,
                // Nokia chrome: 2px solid currentColor in every state.
                // MUI's default unfocused border is rgba(0,0,0,0.23) — soft
                // grey that reads as "disabled" and creates a jarring split
                // when one field is focused (bold black) and others aren't
                // (washed out). Force the border to currentColor across
                // resting / hover / focus so every TextField looks like a
                // Nokia LCD field regardless of focus state.
                '& .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 2,
                  borderColor: 'currentColor',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 2,
                  borderColor: 'currentColor',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 2,
                  borderColor: 'currentColor',
                },
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                textTransform: 'uppercase',
                fontWeight: 400,
                fontFamily: font.display,
                letterSpacing: 1,
              },
            },
          },
          MuiLink: {
            styleOverrides: {
              root: {
                color: darkMode ? color.linkDark : color.linkLight,
                textUnderlineOffset: 2,
              },
            },
          },
        },
      }),
    [mode, darkMode],
  );

  return (
    <ColorModeContext.Provider value={{ darkMode, toggleColorMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
